import React, { useState } from 'react';
import { ApiService } from '../services/apiService';
import './ReferralExportForm.css';

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'in_review', label: 'In Review' },
  { value: 'waiting_on_info', label: 'Waiting on Info' },
  { value: 'ready_for_indici', label: 'Ready for Indici' },
  { value: 'completed', label: 'Completed' },
];

interface FieldDefinition {
  value: string;
  label: string;
}

const FIELD_OPTIONS: FieldDefinition[] = [
  // Referral fields
  { value: 'received_at', label: 'Received At' },
  { value: 'created_at', label: 'Created At' },
  { value: 'updated_at', label: 'Updated At' },
  { value: 'status', label: 'Status' },
  { value: 'sub_status', label: 'Sub Status' },
  { value: 'priority', label: 'Priority' },
  { value: 'referral_type', label: 'Referral Type' },
  { value: 'body_part', label: 'Body Part' },
  { value: 'body_side', label: 'Body Side' },
  { value: 'funder', label: 'Funder' },
  { value: 'is_acc_related', label: 'Is ACC Related' },
  { value: 'source', label: 'Source' },
  { value: 'referring_provider_name', label: 'Referring Provider Name' },
  { value: 'referring_provider_org', label: 'Referring Provider Org' },
  { value: 'triage_category_final', label: 'Triage Category (Final)' },
  { value: 'triage_approved', label: 'Triage Approved' },
  { value: 'is_escalated', label: 'Is Escalated' },
  { value: 'is_complete', label: 'Is Complete' },
  // Triage data fields (from triage_data table)
  { value: 'triage_category_suggested', label: 'Triage Category (AI Suggested)' },
  { value: 'complexity', label: 'Complexity (acute / simple / complex)' },
  { value: 'confidence_percentage', label: 'AI Confidence %' },
  { value: 'provisional_diagnosis', label: 'Provisional Diagnosis' },
  // Computed fields
  { value: 'triage_comments', label: 'Triage Comments' },
];

const ALL_FIELD_VALUES = FIELD_OPTIONS.map((f) => f.value);

// Default fields to pre-select (all fields)
const DEFAULT_SELECTED_FIELDS = ALL_FIELD_VALUES;

export const ReferralExportForm: React.FC = () => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set());
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set(DEFAULT_SELECTED_FIELDS));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleStatus = (value: string) => {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  };

  const toggleField = (value: string) => {
    setSelectedFields((prev) => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  };

  const selectAllFields = () => setSelectedFields(new Set(ALL_FIELD_VALUES));
  const selectNoneFields = () => setSelectedFields(new Set());

  const handleExport = async () => {
    if (selectedFields.size === 0) {
      setError('Select at least one field to export.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const { blob, filename } = await ApiService.exportReferralsCsv({
        fields: ALL_FIELD_VALUES.filter((f) => selectedFields.has(f)),
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        statuses: selectedStatuses.size > 0 ? Array.from(selectedStatuses) : undefined,
      });

      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="export-form">
      {/* Date range */}
      <div className="export-form-section">
        <div className="export-form-section-title">Date Range</div>
        <div className="export-date-range">
          <div className="export-date-field">
            <label htmlFor="export-date-from">From</label>
            <input
              id="export-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="export-date-field">
            <label htmlFor="export-date-to">To</label>
            <input
              id="export-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </div>
        <p style={{ fontSize: '0.8125rem', color: '#6c757d', marginTop: '0.4rem' }}>
          Filters on <strong>received_at</strong>. Leave blank to include all dates.
        </p>
      </div>

      {/* Status filter */}
      <div className="export-form-section">
        <div className="export-form-section-title">Status Filter</div>
        <p style={{ fontSize: '0.8125rem', color: '#6c757d', marginBottom: '0.6rem' }}>
          No selection includes all statuses.
        </p>
        <div className="export-status-list">
          {STATUS_OPTIONS.map((s) => (
            <label key={s.value} className="export-checkbox-item">
              <input
                type="checkbox"
                checked={selectedStatuses.has(s.value)}
                onChange={() => toggleStatus(s.value)}
              />
              {s.label}
            </label>
          ))}
        </div>
      </div>

      {/* Field selection */}
      <div className="export-form-section">
        <div className="export-form-section-title">
          Fields ({selectedFields.size} / {FIELD_OPTIONS.length} selected)
        </div>
        <div className="export-field-actions">
          <button type="button" className="export-btn-text" onClick={selectAllFields}>
            Select All
          </button>
          <span style={{ color: '#dee2e6' }}>|</span>
          <button type="button" className="export-btn-text" onClick={selectNoneFields}>
            Select None
          </button>
        </div>
        <div className="export-field-grid">
          {FIELD_OPTIONS.map((f) => (
            <label key={f.value} className="export-checkbox-item">
              <input
                type="checkbox"
                checked={selectedFields.has(f.value)}
                onChange={() => toggleField(f.value)}
              />
              {f.label}
            </label>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="export-form-footer">
        <button
          type="button"
          className="export-btn-submit"
          onClick={handleExport}
          disabled={loading || selectedFields.size === 0}
        >
          {loading ? 'Exporting…' : 'Export CSV'}
        </button>
        {error && <span className="export-error-message">{error}</span>}
      </div>
    </div>
  );
};
